import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../App";
import { Button } from "./ui/button";
import { Zap, CreditCard, ShoppingBag, X, TrendingUp, ArrowRight } from "lucide-react";

const ACTION_ICONS = {
  upgrade: Zap,
  buy_credits: CreditCard,
  buy_addon: ShoppingBag,
};

const UpgradeNudge = () => {
  const navigate = useNavigate();
  const [nudge, setNudge] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchNudge();
  }, []);

  const fetchNudge = async () => {
    try {
      const res = await fetch(`${API}/analytics/upgrade-nudge`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.show_nudge) setNudge(data);
      }
    } catch {}
  };

  if (!nudge || dismissed) return null;

  const Icon = ACTION_ICONS[nudge.action] || TrendingUp;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10, height: 0 }}
        animate={{ opacity: 1, y: 0, height: "auto" }}
        exit={{ opacity: 0, y: -10, height: 0 }}
        className="mb-6"
        data-testid="upgrade-nudge-banner"
      >
        <div className="bg-gradient-to-r from-crimson/10 via-crimson/5 to-transparent border border-crimson/20 p-4 sm:p-5 relative">
          {/* Dismiss */}
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors"
            data-testid="nudge-dismiss"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Icon + Message */}
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-crimson/20 border border-crimson/30 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-crimson" />
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium leading-relaxed" data-testid="nudge-message">
                  {nudge.message}
                </p>

                {/* Top blocked features */}
                {nudge.top_blocked_features?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {nudge.top_blocked_features.map((f, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-[#1a1a1a] border border-[#333] text-gray-400 uppercase tracking-wider">
                        {f.feature.length > 40 ? f.feature.slice(0, 40) + "..." : f.feature} ({f.count}x)
                      </span>
                    ))}
                  </div>
                )}

                {/* Recommended plan features */}
                {nudge.recommended_tier_info?.features?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {nudge.recommended_tier_info.features.slice(0, 3).map((f, i) => (
                      <span key={i} className="text-[10px] text-emerald-400">
                        {i > 0 && " · "}{f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* CTA */}
            <div className="shrink-0 flex items-center gap-2">
              {nudge.recommended_tier_info?.price > 0 && (
                <span className="text-xs text-gray-500">
                  from <span className="text-white font-bold">${nudge.recommended_tier_info.price}/mo</span>
                </span>
              )}
              <Button
                onClick={() => navigate(nudge.redirect)}
                className="bg-crimson hover:bg-crimson/80 text-white text-xs uppercase tracking-wider whitespace-nowrap"
                data-testid="nudge-cta"
              >
                {nudge.cta} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpgradeNudge;
